// Create a lesson from nothing.
//
// The form only asks for what a lesson cannot exist without — identity,
// where it sits, and what it claims to teach. Steps and questions are
// added afterwards in the lesson's own dialog, because a single form that
// tried to capture ten steps of varying shape before saving anything
// would mean losing the lot to one validation failure.
//
// Whatever is created is a DRAFT. The server enforces that too; this just
// says so out loud, so nobody expects a half-built lesson to be live.
import { useEffect, useState } from "react";
import { Loader2, Plus, Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAcademyAreas, useCreateConcept } from "@/hooks/api/useAcademy";
import { toast } from "react-toastify";

const FACETS = ["RECOGNITION", "RECALL", "JUDGMENT", "CURRENCY"];
const DIFFICULTIES = ["EASY", "MEDIUM", "HARD"];

// Matches the server's rule exactly. Duplicated deliberately: the client
// copy is there to stop the round trip, the server copy is the one that
// actually protects the data.
function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export default function NewLessonDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (slug: string) => void;
}) {
  const { data: areas } = useAcademyAreas();
  const create = useCreateConcept();

  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [definition, setDefinition] = useState("");
  const [objectives, setObjectives] = useState("");
  const [facet, setFacet] = useState(FACETS[0]);
  const [difficulty, setDifficulty] = useState(DIFFICULTIES[1]);
  const [areaKey, setAreaKey] = useState("");
  const [stageKey, setStageKey] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Suggested from the name until the author edits it themselves, at
  // which point it stops being overwritten — a slug is permanent, so
  // fighting someone who has deliberately chosen one is the wrong
  // default.
  useEffect(() => {
    if (!slugTouched) setSlug(slugify(name));
  }, [name, slugTouched]);

  useEffect(() => {
    if (!open) return;
    setName("");
    setSlug("");
    setSlugTouched(false);
    setDefinition("");
    setObjectives("");
    setFacet(FACETS[0]);
    setDifficulty(DIFFICULTIES[1]);
    setAreaKey("");
    setStageKey("");
    setError(null);
  }, [open]);

  const stages = areas?.find((a) => a.key === areaKey)?.stages ?? [];

  const objectiveList = objectives
    .split("\n")
    .map((o) => o.trim())
    .filter(Boolean);

  const ready =
    !!name.trim() &&
    !!slug.trim() &&
    !!definition.trim() &&
    objectiveList.length > 0 &&
    !!areaKey &&
    !!stageKey;

  const submit = () => {
    setError(null);
    create.mutate(
      {
        slug: slug.trim(),
        name: name.trim(),
        definition: definition.trim(),
        learningObjectives: objectiveList,
        facet,
        difficulty,
        areaKey,
        stageKey,
      },
      {
        onSuccess: (concept) => {
          toast.success("Lesson created as a draft");
          onOpenChange(false);
          // Straight into the lesson, because a lesson with no steps is
          // not finished and the next thing to do is obvious.
          onCreated(concept.slug);
        },
        onError: (e: unknown) => {
          const res = (e as { response?: { data?: { message?: unknown } } })
            ?.response;
          const msg = res?.data?.message;
          setError(
            Array.isArray(msg)
              ? msg.join("\n")
              : typeof msg === "string"
                ? msg
                : "Could not create the lesson."
          );
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] w-[min(96vw,760px)] max-w-[96vw] overflow-y-auto overflow-x-hidden sm:max-w-[760px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="h-5 w-5 text-emerald-600" />
            New lesson
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="The Back Three"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Slug</Label>
              <Input
                value={slug}
                onChange={(e) => {
                  setSlugTouched(true);
                  setSlug(e.target.value);
                }}
                placeholder="back-three"
                className="font-mono text-sm"
              />
              <p className="text-[11px] text-muted-foreground">
                Permanent. Content and progress records reference it.
              </p>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Definition</Label>
            <textarea
              value={definition}
              onChange={(e) => setDefinition(e.target.value)}
              placeholder="One or two sentences saying what this is."
              className="h-20 w-full resize-y rounded-md border bg-transparent p-2.5 text-sm outline-none focus:ring-2 focus:ring-emerald-500/40"
            />
          </div>

          <div className="space-y-1.5">
            <Label>Learning goals</Label>
            <textarea
              value={objectives}
              onChange={(e) => setObjectives(e.target.value)}
              placeholder={"One per line.\nTell a back three from a back four"}
              className="h-24 w-full resize-y rounded-md border bg-transparent p-2.5 text-sm outline-none focus:ring-2 focus:ring-emerald-500/40"
            />
            <p className="text-[11px] text-muted-foreground">
              {objectiveList.length} goal
              {objectiveList.length === 1 ? "" : "s"}
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Area</Label>
              <Select
                value={areaKey}
                onValueChange={(v) => {
                  setAreaKey(v);
                  // Stages belong to an area, so a stage chosen under the
                  // previous one is now meaningless.
                  setStageKey("");
                }}
              >
                <SelectTrigger className="cursor-pointer">
                  <SelectValue placeholder="Choose an area" />
                </SelectTrigger>
                <SelectContent>
                  {(areas ?? []).map((a) => (
                    <SelectItem key={a.key} value={a.key}>
                      {a.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label>Stage</Label>
              <Select
                value={stageKey}
                onValueChange={setStageKey}
                disabled={!areaKey}
              >
                <SelectTrigger className="cursor-pointer">
                  <SelectValue
                    placeholder={areaKey ? "Choose a stage" : "Pick an area first"}
                  />
                </SelectTrigger>
                <SelectContent>
                  {stages.map((s) => (
                    <SelectItem key={s.key} value={s.key}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label>Facet</Label>
              <Select value={facet} onValueChange={setFacet}>
                <SelectTrigger className="cursor-pointer">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FACETS.map((f) => (
                    <SelectItem key={f} value={f}>
                      {f}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label>Difficulty</Label>
              <Select value={difficulty} onValueChange={setDifficulty}>
                <SelectTrigger className="cursor-pointer">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DIFFICULTIES.map((d) => (
                    <SelectItem key={d} value={d}>
                      {d}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {error && (
            <div className="whitespace-pre-wrap rounded border border-red-500/30 bg-red-500/10 p-2.5 text-xs text-red-600">
              {error}
            </div>
          )}

          <div className="flex items-center gap-3 border-t pt-4">
            <Button
              className="cursor-pointer gap-1.5"
              disabled={!ready || create.isPending}
              onClick={submit}
            >
              {create.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
              Create draft
            </Button>
            <span className="text-xs text-muted-foreground">
              Created as a draft. Add the steps, then publish.
            </span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
